export const SAVE_PERSISTED_EVENT = "viewr:savePersistedToGcs";
export const SAVE_PERSIST_FAILED_EVENT = "viewr:savePersistFailed";
export const LEGACY_SAVE_JSON_COMPLETE_EVENT = "viewr:saveJsonComplete";

export interface SavePersistedEventDetail {
  gcsPath?: string;
  jobId?: string;
  source?: string;
  suppressToast?: boolean;
  skipThumbnailAfterPersist?: boolean;
  saveAttemptId?: string;
  reason?: string;
  originMutationId?: string;
  signature?: string;
  timestamp?: number;
}

export interface SaveFailedEventDetail {
  gcsPath?: string;
  saveContext?: string;
  error?: string;
  saveAttemptId?: string;
  reason?: string;
  originMutationId?: string;
  timestamp?: number;
}

export type SavePersistedEvent = CustomEvent<SavePersistedEventDetail>;
export type SaveFailedEvent = CustomEvent<SaveFailedEventDetail>;

export function dispatchSavePersisted(
  detail: SavePersistedEventDetail,
): void {
  window.dispatchEvent(
    new CustomEvent(SAVE_PERSISTED_EVENT, {
      detail,
    }),
  );
}

export function dispatchSavePersistFailed(detail: SaveFailedEventDetail): void {
  window.dispatchEvent(
    new CustomEvent(SAVE_PERSIST_FAILED_EVENT, {
      detail,
    }),
  );
}

export function dispatchLegacySaveJsonComplete(detail: unknown): void {
  window.dispatchEvent(
    new CustomEvent(LEGACY_SAVE_JSON_COMPLETE_EVENT, {
      detail,
    }),
  );
}

export function subscribeSavePersisted(
  listener: (event: SavePersistedEvent) => void,
): () => void {
  const wrapped = listener as EventListener;
  window.addEventListener(SAVE_PERSISTED_EVENT, wrapped);
  return () => window.removeEventListener(SAVE_PERSISTED_EVENT, wrapped);
}

export function subscribeSaveFailed(
  listener: (event: SaveFailedEvent) => void,
): () => void {
  const wrapped = listener as EventListener;
  window.addEventListener(SAVE_PERSIST_FAILED_EVENT, wrapped);
  return () => window.removeEventListener(SAVE_PERSIST_FAILED_EVENT, wrapped);
}

export function subscribeLegacySaveJsonComplete(
  listener: (event: CustomEvent<unknown>) => void,
): () => void {
  const wrapped = listener as EventListener;
  window.addEventListener(LEGACY_SAVE_JSON_COMPLETE_EVENT, wrapped);
  return () => window.removeEventListener(LEGACY_SAVE_JSON_COMPLETE_EVENT, wrapped);
}

export function waitForSavePersisted({
  matches,
  timeoutMs = 30000,
  signal,
}: {
  matches?: (detail: SavePersistedEventDetail) => boolean;
  timeoutMs?: number;
  signal?: AbortSignal;
} = {}): Promise<SavePersistedEventDetail> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Save wait aborted", "AbortError"));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("save persisted wait timed out"));
    }, timeoutMs);

    const onPersisted = (event: SavePersistedEvent) => {
      const detail = event.detail ?? {};
      if (matches && !matches(detail)) return;
      cleanup();
      resolve(detail);
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException("Save wait aborted", "AbortError"));
    };

    const unsubscribePersisted = subscribeSavePersisted(onPersisted);
    const cleanup = () => {
      clearTimeout(timeoutId);
      unsubscribePersisted();
      signal?.removeEventListener("abort", onAbort);
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function waitForSaveSettled({
  matches,
  timeoutMs = 30000,
  signal,
}: {
  matches?: (
    detail: SavePersistedEventDetail | SaveFailedEventDetail,
    status: "persisted" | "failed",
  ) => boolean;
  timeoutMs?: number;
  signal?: AbortSignal;
} = {}): Promise<
  | { status: "persisted"; detail: SavePersistedEventDetail }
  | { status: "failed"; detail: SaveFailedEventDetail }
> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Save wait aborted", "AbortError"));
      return;
    }

    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("save settled wait timed out"));
    }, timeoutMs);

    const onPersisted = (event: SavePersistedEvent) => {
      const detail = event.detail ?? {};
      if (matches && !matches(detail, "persisted")) return;
      cleanup();
      resolve({ status: "persisted", detail });
    };

    const onFailed = (event: SaveFailedEvent) => {
      const detail = event.detail ?? {};
      if (matches && !matches(detail, "failed")) return;
      cleanup();
      resolve({ status: "failed", detail });
    };

    const onAbort = () => {
      cleanup();
      reject(new DOMException("Save wait aborted", "AbortError"));
    };

    const unsubscribePersisted = subscribeSavePersisted(onPersisted);
    const unsubscribeFailed = subscribeSaveFailed(onFailed);

    const cleanup = () => {
      clearTimeout(timeoutId);
      unsubscribePersisted();
      unsubscribeFailed();
      signal?.removeEventListener("abort", onAbort);
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
